// standalone-uploader.ts — the item-media dropzone uploader OUTSIDE the per-item manager.
//
// Some pages just want the nice multi-file drag-drop UX (previews + real upload-progress bar) with
// no galleries, no DB rows and no item picker — e.g. the admin "Upload Icons" page, which pushes
// static UI icons straight to the CDN. This adopts a tiny PHP-rendered mount:
//
//   <div data-ft-standalone-uploader data-ft-upload-endpoint="…/upload_icons_inc.php">
//       <div data-ft-uploader></div>            <!-- dropzone host (optional; falls back to the mount) -->
//       <div data-ft-uploader-results></div>    <!-- where uploaded CDN links get listed (optional) -->
//   </div>
//
// and reuses mountUploader('icon'). The only delta vs the manager: no item_id is posted and the
// returned {name,url} files are listed back as CDN links instead of appended to a gallery.

import { mountUploader, type UploadedFile } from './uploader';
import type { ItemMediaConfig } from './types';

// mountUploader only reads config.uploadEndpoint for the icon kind; everything else is unused here
// but the type wants the full shape, so fill the rest with empty strings.
function minimalConfig(uploadEndpoint: string): ItemMediaConfig {
    return {
        mediaType: '',
        attachedItemType: '',
        uploadEndpoint,
        videoUploadEndpoint: '',
        picPrimaryEndpoint: '',
        picDeleteEndpoint: '',
        picHardDeleteEndpoint: '',
        picEditBase: '',
        videoPrimaryEndpoint: '',
        videoDeleteEndpoint: '',
        icons: { tabPictures: '', tabVideos: '', tabUpload: '', primary: '', edit: '', delete: '' },
        statusDivId: 'div_item_media_status',
    };
}

function mountOne(root: HTMLElement): void {
    if (root.getAttribute('data-ft-standalone-uploader-mounted') === '1') return;
    root.setAttribute('data-ft-standalone-uploader-mounted', '1');

    const endpoint = root.getAttribute('data-ft-upload-endpoint') || '';
    if (!endpoint) {
        console.error('[standalone-uploader] missing data-ft-upload-endpoint', root);
        return;
    }

    const host = root.querySelector<HTMLElement>('[data-ft-uploader]') ?? root;
    let results = root.querySelector<HTMLElement>('[data-ft-uploader-results]');
    if (!results) {
        results = document.createElement('div');
        results.className = 'mt-3';
        root.appendChild(results);
    }
    const resultsEl = results;

    mountUploader(host, {
        config: minimalConfig(endpoint),
        itemId: '',
        kind: 'icon',
        onUploadedIcons: (icons: UploadedFile[]) => {
            for (const ic of icons) {
                const row = document.createElement('div');
                row.className = 'alert alert-success py-1 px-2 mb-2 small';
                if (ic.url) {
                    const link = document.createElement('a');
                    link.href = ic.url;
                    link.target = '_blank';
                    link.rel = 'noopener';
                    link.textContent = ic.url;
                    row.append('✓ ' + (ic.name || '') + ' → ', link);
                } else {
                    row.textContent = '✓ ' + (ic.name || '');
                }
                resultsEl.appendChild(row);
            }
        },
    });
}

export function mountStandaloneUploaders(): void {
    document
        .querySelectorAll<HTMLElement>('[data-ft-standalone-uploader]:not([data-ft-standalone-uploader-mounted="1"])')
        .forEach(mountOne);
}
